import React, { useCallback, useEffect, useState } from 'react';

import { createProfileManager } from '../../../di';
import { useAppDispatch, useErrorCallback, useNavigateByName } from '../../../hooks';
import { set } from '../../../store/overlay';
import { RouteDictionary } from '../../../utils';
import { Button, PrimaryButton } from '../../common';
import { PrimaryButtonColor } from '../../common/buttons/decorators/PrimaryButton/PrimaryButton';
import { OnboardingMobileFooter } from '../OnboardingMobileFooter';
import { useOnboarding } from '../OnboardingModalContext';
import styles from './OnboardingFinal.module.scss';

const OnboardingFinal = () => {
  const { currentStep, changeCurrentStep, setIsActive, isMobile, jsonData } = useOnboarding();
  const navigate = useNavigateByName();
  const dispatch = useAppDispatch();
  const handleError = useErrorCallback();
  const [_, setError] = useState<string>();
  const [showCalendlyMobile, setShowCalendlyMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Состояние загрузки

  const handlePreviousStep = () => changeCurrentStep(currentStep - 1);

  const loadData = useCallback(async () => {
    try {
      if (!jsonData) return;
      const profileManager = await createProfileManager();
      await profileManager.addOnboardingAnswers(jsonData);
    } catch (err) {
      if (err instanceof Error) {
        await handleError(err);
        setError(err.message);
      }
    }
  }, [jsonData, handleError]);

  const validateCurrentStep = (): boolean => {
    if (!jsonData) {
      return false;
    }
    const { selectedPurposes, projectsCount, sourceDetails } = jsonData;

    if (isMobile) {
      if (currentStep === 1) {
        return true;
      } else if (currentStep === 2) {
        return selectedPurposes && selectedPurposes.length > 0;
      } else if (currentStep === 3) {
        return !!projectsCount;
      } else if (currentStep === 4) {
        return !!sourceDetails && !!sourceDetails.option;
      }
    } else {
      if (currentStep === 1) {
        return selectedPurposes && selectedPurposes.length > 0;
      } else if (currentStep === 2) {
        return !!projectsCount;
      } else if (currentStep === 3) {
        return !!sourceDetails && !!sourceDetails.option;
      }
    }

    return true;
  };

  const handleClose = async () => {
    setIsLoading(true);
    await loadData();
    setIsActive(false);
    dispatch(set(false));
    navigate(RouteDictionary.IDENTIFICATION);
    setIsLoading(false);
  };

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    script.onload = () => {
      if (window.Calendly) {
        window.Calendly.initInlineWidget({
          url: 'https://calendly.com/ksutarasova260/30min?hide_event_type_details=1&hide_gdpr_banner=1',
          parentElement: document.getElementById('calendly'),
          prefill: {},
          utm: {},
        });
      }
    };

    return () => {
      document.body.removeChild(script);
    };
  }, [currentStep]);

  return (
    <div className={`${styles.finalContent} ${showCalendlyMobile ? styles.calendlyActive : ''}`}>
      <div
        className={`${styles.finalText} ${isMobile && showCalendlyMobile ? styles.calendlyMobileHide : ''}`}
      >
        <div className={styles.finalTitle}>
          Связаться с инвестиционным менеджером, чтобы быстрее разобраться в инструменте
        </div>
        <div className={styles['text-gray-3']}>
          В Conomica есть возможность связаться с инвестиционным менеджером, который поможет
          разобраться с инструментом и выбрать стратегии инвестирования, которые подойдут под ваш
          запрос
        </div>
      </div>

      <PrimaryButton color={PrimaryButtonColor.GRAY} className={styles.previousButton}>
        <Button onClick={handlePreviousStep}>Назад</Button>
      </PrimaryButton>

      {isMobile && !showCalendlyMobile && (
        <PrimaryButton
          color={PrimaryButtonColor.BLUE}
          expanded={true}
          large={true}
          className={styles.calendlyButton__mobile}
        >
          <Button onClick={() => setShowCalendlyMobile(true)}>Выбрать время для встречи</Button>
        </PrimaryButton>
      )}

      <div
        id="calendly"
        className={`${styles.calendly__inlineWidget} ${isMobile && !showCalendlyMobile ? styles.calendlyMobileHide : ''}`}
      ></div>

      <div className={`'text-gray-3' ${styles.onboardingModal__stepsCount}`}>
        Шаг {currentStep} из {currentStep}
      </div>

      {!isMobile && (
        <PrimaryButton color={PrimaryButtonColor.GREEN} className={styles.nextButton}>
          <Button onClick={handleClose}>{isLoading ? 'Загрузка...' : 'Закрыть'}</Button>
        </PrimaryButton>
      )}

      {isMobile && (
        <OnboardingMobileFooter
          handleNextStep={handleClose}
          validateCurrentStep={validateCurrentStep}
          currentStep={currentStep}
          stepsCount={currentStep + 1}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default OnboardingFinal;
